/** Persyaratan template + submission live queries. */

import { useMemo } from 'react';
import { COL } from '../../collections';
import { where, orderBy } from '../../collections';
import { useLiveQuery } from '../hooks';
import {
  resolvePersyaratanTemplate,
  type ConsultIzinInfo,
  type PersyaratanScopeContext,
} from '../../persyaratanRules';
import type { PersyaratanDoc, PersyaratanStage, PersyaratanSubmissionDoc } from '../../types';

export type { ConsultIzinInfo, PersyaratanScopeContext };

export function useAllPersyaratanTemplates() {
  return useLiveQuery<PersyaratanDoc>(COL.persyaratan, [orderBy('updatedAt', 'desc')]);
}

/** Active persyaratan template for a stage, resolved against a skripsi scope. */
export function usePersyaratanTemplate(
  stage: PersyaratanStage | null,
  scope: PersyaratanScopeContext = {},
) {
  const all = useAllPersyaratanTemplates();
  const data = useMemo(
    () => resolvePersyaratanTemplate(all.data, stage, scope),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all.data, stage, scope.prodiId, scope.jurusanId, scope.fakultas],
  );
  return { data, loading: all.loading, error: all.error };
}

/**
 * Which level administers a stage's request flow. Stages whose template is
 * fakultas-scoped are handled at the faculty level (documents require a dekan
 * assignment); everything else stays at the prodi level. With no template the
 * flow stays at the prodi level (the historical default).
 */

/** Latest persyaratan submission of a skripsi at a stage. */
export function usePersyaratanSubmission(skripsiId: string | null, stage: PersyaratanStage | null) {
  return useLiveQuery<PersyaratanSubmissionDoc>(
    skripsiId && stage ? COL.persyaratanSubmission : null,
    skripsiId && stage ? [where('skripsiId', '==', skripsiId), where('stage', '==', stage)] : [],
    Boolean(skripsiId && stage),
  );
}
